(() => {
  const ROOT_SELECTOR = '[data-pm-hub-selector]';
  const STORAGE_KEY = 'purefect.deliveryHub.v1';
  const CART_ATTRIBUTE_NAME = 'Delivery hub';
  const HUB_CHANGE_EVENT = 'pm:hub:change';

  const safeJson = (value, fallback = null) => {
    if (typeof value !== 'string' || value.trim() === '') return fallback;
    try {
      return JSON.parse(value);
    } catch (_error) {
      return fallback;
    }
  };

  const hubId = (label) => label
    .toLocaleLowerCase('en-US')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '') || 'hub';

  class PurefectHubSelector {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('[data-pm-hub-dialog]');
      this.form = root.querySelector('[data-pm-hub-form]');
      this.closeButton = root.querySelector('[data-pm-hub-close]');
      this.optionsContainer = root.querySelector('[data-pm-hub-options]');
      this.feedback = root.querySelector('[data-pm-hub-feedback]');
      this.saveButton = root.querySelector('[data-pm-hub-save]');
      this.recommendationStatus = root.querySelector('[data-pm-hub-recommendation-status]');
      this.locateButton = root.querySelector('[data-pm-hub-locate]');
      this.config = safeJson(root.querySelector('[data-pm-hub-config]')?.textContent, {});
      this.options = this.buildOptions();
      this.selectedHub = this.initialHub();
      this.recommendationCandidates = this.buildRecommendationCandidates();
      const previousHub = this.recommendationCandidates.find(option =>
        option.label.toLocaleLowerCase('en-US') === String(this.config.purchaseHub || '').trim().toLocaleLowerCase('en-US'));
      this.recommendedHubId = previousHub?.id || null;
      this.recommendationSource = previousHub ? 'history' : null;
      this.recommendationMessage = previousHub ? this.config.recommendationHistory : this.config.recommendationInitial;
      this.locationRequested = false;
      this.locationPending = false;
      this.locationRequestVersion = 0;
      this.returnFocusTarget = null;
      this.observerQueued = false;
      this.handleDocumentClick = this.handleDocumentClick.bind(this);
      this.handleMutations = this.handleMutations.bind(this);
      this.renderOptions();
      this.updateExperience();
      this.bindEvents();
      this.syncInitialCartAttribute();
    }

    buildOptions() {
      const values = [this.config.defaultName]
        .concat(String(this.config.additionalOptions || '').split(/\r?\n/))
        .map((value) => value.trim())
        .filter(Boolean);
      const seen = new Set();
      const options = [];

      values.forEach((label, index) => {
        const normalized = label.toLocaleLowerCase('en-US');
        if (seen.has(normalized)) return;
        seen.add(normalized);
        options.push({
          id: hubId(label),
          label,
          description: this.config.standardSchedule,
          isDefault: index === 0,
          configured: true,
          chooseAtCheckout: false
        });
      });

      const cartValue = this.root.dataset.pmHubCartValue?.trim();
      if (cartValue && !window.PurefectTST?.isEvent(cartValue) && !seen.has(cartValue.toLocaleLowerCase('en-US')) && cartValue !== this.config.checkoutLabel) {
        options.push({
          id: hubId(cartValue),
          label: cartValue,
          description: this.config.standardSchedule,
          isDefault: false,
          chooseAtCheckout: false
        });
      }

      // A neutral choice keeps first-time visitors from being assigned a physical hub.
      if (!options.some(option => option.chooseAtCheckout)) {
        options.push({
          id: 'choose-at-checkout',
          label: this.config.checkoutLabel,
          description: this.config.checkoutDescription,
          isDefault: false,
          chooseAtCheckout: true
        });
      }

      const event = window.PurefectTST;
      if (event && (event.isOpen() || event.isEvent(cartValue) || event.isEvent(safeJson(this.readStorage(), {})))) {
        options.push({id:'tst-in-service',label:event.label,description:event.isOpen() ? 'Friday, September 25 · Order by Tuesday, Sept 22, 11:59 PM ET · Code TST25' : 'Ordering closed. Choose another pickup option.',isDefault:false,chooseAtCheckout:false});
      }
      return options;
    }

    initialHub() {
      if (new URLSearchParams(location.search).get('tst') === '1' && window.PurefectTST?.isOpen()) {
        return this.options.find(option => option.id === 'tst-in-service');
      }
      const cartValue = this.root.dataset.pmHubCartValue?.trim();
      const cartMatch = this.options.find((option) => option.label === cartValue);
      if (cartMatch) return cartMatch;

      const stored = safeJson(this.readStorage(), {});
      const storedMatch = this.options.find((option) => option.id === stored.id || option.label === stored.label);
      if (storedMatch) return storedMatch;

      return this.options.find((option) => option.chooseAtCheckout);
    }

    buildRecommendationCandidates() {
      const locations = Array.isArray(this.config.hubLocations) ? this.config.hubLocations : [];
      return this.options.filter(option => option.configured && !option.chooseAtCheckout).flatMap(option => {
        const location = locations.find(item => typeof item?.name === 'string' &&
          item.name.trim().toLocaleLowerCase('en-US') === option.label.toLocaleLowerCase('en-US'));
        return location ? [{ ...option, latitude: location.latitude, longitude: location.longitude }] : [];
      });
    }

    updateRecommendation() {
      // Only change badges: a late location response must not replace a pending radio choice or focus.
      const badgeLabel = this.recommendationSource === 'history' ? this.config.historyBadge : this.config.defaultBadge;
      this.optionsContainer.querySelectorAll('[data-pm-hub-badge]').forEach(badge => {
        badge.hidden = badge.dataset.pmHubBadge !== this.recommendedHubId;
        badge.textContent = String(badgeLabel || '');
      });
      const recommended = this.options.find(option => option.id === this.recommendedHubId);
      if (this.recommendationStatus) {
        this.recommendationStatus.textContent = String(this.recommendationMessage || '').replace('%%hub%%', recommended?.label || '');
      }
      if (this.locateButton) {
        this.locateButton.hidden = Boolean(this.recommendedHubId) || !navigator.geolocation || window.isSecureContext === false;
        this.locateButton.disabled = this.locationPending;
        this.locateButton.setAttribute('aria-busy', String(this.locationPending));
      }
    }

    requestRecommendation(retry = false) {
      if (this.recommendationSource === 'history' || this.locationPending || (this.locationRequested && !retry)) return;
      this.locationRequested = true;
      if (!navigator.geolocation || window.isSecureContext === false) {
        this.recommendationMessage = this.config.recommendationUnsupported;
        this.updateRecommendation();
        return;
      }
      this.locationPending = true;
      this.recommendedHubId = null;
      this.recommendationSource = null;
      this.recommendationMessage = this.config.recommendationLoading;
      const requestVersion = ++this.locationRequestVersion;
      this.updateRecommendation();
      const finish = (hub, message) => {
        if (requestVersion !== this.locationRequestVersion || this.recommendationSource === 'history') return;
        this.locationPending = false;
        this.recommendedHubId = hub?.id || null;
        this.recommendationSource = hub ? 'location' : null;
        this.recommendationMessage = message;
        this.updateRecommendation();
      };
      const validCoordinates = (latitude, longitude) => Number.isFinite(latitude) && Number.isFinite(longitude) &&
        latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;
      try {
        navigator.geolocation.getCurrentPosition(position => {
          const latitude = position?.coords?.latitude;
          const longitude = position?.coords?.longitude;
          if (!validCoordinates(latitude, longitude)) return finish(null, this.config.recommendationUnavailable);
          const radians = degrees => degrees * Math.PI / 180;
          let nearest = null, nearestDistance = Infinity;
          this.recommendationCandidates.forEach(option => {
            if (!validCoordinates(option.latitude, option.longitude)) return;
            const latitudeDifference = radians(option.latitude - latitude);
            const longitudeDifference = radians(option.longitude - longitude);
            const distance = Math.sin(latitudeDifference / 2) ** 2 + Math.cos(radians(latitude)) *
              Math.cos(radians(option.latitude)) * Math.sin(longitudeDifference / 2) ** 2;
            if (distance < nearestDistance) { nearest = option; nearestDistance = distance; }
          });
          // Coordinates are used in this calculation only; they are never persisted or sent to the store.
          finish(nearest, nearest ? this.config.recommendationNearby : this.config.recommendationUnavailable);
        }, error => finish(null, error?.code === 1 ? this.config.recommendationPermission : this.config.recommendationUnavailable),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 0 });
      } catch (_error) {
        finish(null, this.config.recommendationUnavailable);
      }
    }

    readStorage() {
      try {
        return window.localStorage.getItem(STORAGE_KEY);
      } catch (_error) {
        return null;
      }
    }

    writeStorage(hub) {
      try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ id: hub.id, label: hub.label }));
      } catch (_error) {
        return;
      }
    }

    renderOptions() {
      this.optionsContainer.innerHTML = this.options.map((option) => {
        const isChecked = option.id === this.selectedHub.id;
        const badge = `<span class="pm-hub-selector__badge" data-pm-hub-badge="${this.escapeHtml(option.id)}"${option.id === this.recommendedHubId ? '' : ' hidden'}>${this.escapeHtml(this.config.defaultBadge)}</span>`;
        return `
          <label class="pm-hub-selector__option">
            <input type="radio" name="pm-delivery-hub" value="${this.escapeHtml(option.id)}"${isChecked ? ' checked' : ''}>
            <span class="pm-hub-selector__radio" aria-hidden="true"></span>
            <span class="pm-hub-selector__option-copy">
              <span class="pm-hub-selector__option-heading"><strong>${this.escapeHtml(option.label)}</strong>${badge}</span>
              <small>${this.escapeHtml(option.description)}</small>
            </span>
          </label>
        `;
      }).join('');
      this.updateRecommendation();
    }

    escapeHtml(value) {
      const element = document.createElement('span');
      element.textContent = String(value || '');
      return element.innerHTML;
    }

    bindEvents() {
      document.addEventListener('pm:delivery-schedule:ready', () => this.updateExperience(), { once: true });
      document.addEventListener('click', this.handleDocumentClick);
      this.closeButton.addEventListener('click', () => this.close());
      this.form.addEventListener('submit', (event) => this.save(event));
      this.locateButton?.addEventListener('click', () => this.requestRecommendation(true));
      this.dialog.addEventListener('cancel', (event) => {
        event.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (event) => {
        if (event.target === this.dialog) this.close();
      });
      this.observer = new MutationObserver(this.handleMutations);
      const observationRoot = document.body || document.documentElement;
      if (observationRoot) this.observer.observe(observationRoot, { childList: true, subtree: true });
    }

    handleDocumentClick(event) {
      const trigger = event.target.closest('[data-pm-hub-open]');
      if (!trigger) return;
      event.preventDefault();
      this.open(trigger);
    }

    handleMutations() {
      if (this.observerQueued) return;
      this.observerQueued = true;
      window.requestAnimationFrame(() => {
        this.observerQueued = false;
        this.injectContextPromises();
      });
    }

    open(trigger) {
      this.returnFocusTarget = trigger;
      this.feedback.textContent = '';
      this.renderOptions();
      document.body.classList.add('pm-hub-selector-open');
      if (typeof this.dialog.showModal === 'function') {
        this.dialog.showModal();
      } else {
        this.dialog.setAttribute('open', '');
      }
      this.dialog.querySelector('input:checked')?.focus();
      this.requestRecommendation();
    }

    close() {
      if (typeof this.dialog.close === 'function' && this.dialog.open) {
        this.dialog.close();
      } else {
        this.dialog.removeAttribute('open');
      }
      document.body.classList.remove('pm-hub-selector-open');
      if (this.returnFocusTarget?.isConnected) this.returnFocusTarget.focus();
    }

    async save(event) {
      event.preventDefault();
      const selectedId = new FormData(this.form).get('pm-delivery-hub');
      const nextHub = this.options.find((option) => option.id === selectedId);
      if (!nextHub) return;
      const previousHub = this.selectedHub;
      this.setSaving(true);
      this.feedback.textContent = this.config.saving;

      try {
        await this.updateCartAttribute(nextHub);
        this.selectedHub = nextHub;
        this.writeStorage(nextHub);
        this.updateExperience();
        this.feedback.textContent = this.config.saved;
        window.setTimeout(() => this.close(), 350);
      } catch (_error) {
        this.selectedHub = previousHub;
        this.feedback.textContent = window.PurefectOrderSupport.format(this.config.saveError);
      } finally {
        this.setSaving(false);
      }
    }

    setSaving(isSaving) {
      this.saveButton.disabled = isSaving;
      this.saveButton.setAttribute('aria-busy', String(isSaving));
    }

    async updateCartAttribute(hub) {
      if (window.PurefectClubCart?.isContext()) return window.PurefectClubCart.pickup(hub);
      const event = window.PurefectTST;
      const isEvent = event?.isEvent(hub);
      if (isEvent && !event.isOpen()) throw new Error('TST ordering is closed');
      const currentResponse = await fetch((window.Shopify?.routes?.root || '/') + 'cart.js', { cache: 'no-store' });
      if (!currentResponse.ok) throw new Error('Unable to read cart');
      const currentCart = await currentResponse.json();
      if (currentCart.item_count) await window.PurefectPickupCheckout?.sync(currentCart, hub);
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({ attributes: {
          [CART_ATTRIBUTE_NAME]: hub.label,
          'Pickup date': isEvent ? event.dateLabel : (hub.chooseAtCheckout ? '' : (this.nextDelivery(false)?.label || '')),
          'Pickup collection spot': isEvent ? 'TST In-Service at GCPS Instructional Support Center' : (hub.chooseAtCheckout ? '' : 'Staff Breakroom'),
          'Ordering event': isEvent ? 'TST In-Service — September 25, 2026' : '',
          'Ordering cutoff': isEvent ? event.cutoff : 'Friday, 11:59 PM Eastern Time'
        } })
      });
      if (!response.ok) throw new Error('Unable to save hub');
      const cart = await response.json();
      if (cart?.errors) throw new Error('Unable to save hub');
      return cart;
    }

    syncInitialCartAttribute() {
      if (window.PurefectClubCart?.isContext()) return;
      this.writeStorage(this.selectedHub);
      this.updateCartAttribute(this.selectedHub).catch(() => undefined);
    }

    nextDelivery(useEvent = true) {
      if (useEvent && window.PurefectTST?.isEvent(this.selectedHub)) return {key:window.PurefectTST.dateKey,label:window.PurefectTST.dateLabel};
      const schedule = window.PurefectDeliverySchedule;
      const key = schedule?.nextDeliveryKeyFor?.();
      if (!key) return null;
      return {
        key,
        label: schedule.readableDate(key, { weekday: 'long', month: 'long', day: 'numeric' })
      };
    }

    promiseText() {
      const delivery = this.nextDelivery();
      if (!delivery) return this.config.schedulePending;
      if (this.selectedHub.chooseAtCheckout) {
        return this.config.deliveryCheckout.replace('%%date%%', delivery.label);
      }
      return this.config.deliveryTo
        .replace('%%date%%', delivery.label)
        .replace('%%hub%%', this.selectedHub.label);
    }

    statusText() {
      if (this.selectedHub.chooseAtCheckout) return this.config.statusCheckout;
      return this.config.statusTo.replace('%%hub%%', this.selectedHub.label);
    }

    updateExperience() {
      document.documentElement.dataset.pmHub = this.selectedHub.id;
      document.querySelectorAll('[data-pm-combined-date]').forEach(element => {
        element.textContent = this.selectedHub.chooseAtCheckout ? 'Choose location at checkout' : (this.nextDelivery()?.label || 'View pickup dates');
      });
      document.querySelectorAll('body [data-pm-hub-name]').forEach((element) => {
        element.textContent = this.selectedHub.label;
      });
      document.querySelectorAll('[data-pm-delivery-status-note]').forEach((element) => {
        if (element.closest('[data-pm-delivery-status]')?.classList.contains('pm-delivery-status--pending')) return;
        element.textContent = this.statusText();
      });
      this.injectContextPromises();
      document.dispatchEvent(new CustomEvent(HUB_CHANGE_EVENT, {
        detail: {
          id: this.selectedHub.id,
          name: this.selectedHub.label,
          chooseAtCheckout: this.selectedHub.chooseAtCheckout,
          nextDelivery: this.nextDelivery()
        }
      }));
    }

    createPromise(context) {
      const element = document.createElement('aside');
      element.className = `pm-hub-promise pm-hub-promise--${context}`;
      element.dataset.pmHubPromise = context;
      element.innerHTML = `
        <span class="pm-hub-promise__icon" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 21s6-5.2 6-11A6 6 0 1 0 6 10c0 5.8 6 11 6 11Z"/><circle cx="12" cy="10" r="2.2"/></svg>
        </span>
        <span class="pm-hub-promise__copy">
          <small>${this.escapeHtml(this.config.promiseLabel)}</small>
          <strong data-pm-hub-promise-text></strong>
          <small>Collect in the Staff Breakroom · Order by Friday, 11:59 PM Eastern Time</small>
        </span>
        <span class="pm-hub-promise__actions"><button type="button" data-pm-hub-open>${this.escapeHtml(this.config.change)}</button><button type="button" data-pm-growth-calendar>Pickup dates</button></span>
      `;
      return element;
    }

    injectContextPromises() {
      const contexts = [
        { name: 'product', targets: document.querySelectorAll('product-info:not(.pm-quick-add-product) .product__info-container > .product__title') },
        { name: 'quick-add', targets: document.querySelectorAll('product-info.pm-quick-add-product .product__info-container > .product__title') },
        { name: 'cart-page', targets: document.querySelectorAll('#main-cart-footer .cart__footer') },
        { name: 'cart-drawer', targets: document.querySelectorAll('cart-drawer .drawer__footer') }
      ];

      contexts.forEach((context) => {
        context.targets.forEach((target) => {
          const container = context.name === 'product' || context.name === 'quick-add'
            ? target.parentElement
            : target;
          if (!container || container.querySelector(`:scope > [data-pm-hub-promise="${context.name}"]`)) return;
          const promise = this.createPromise(context.name);
          if (context.name === 'product' || context.name === 'quick-add') {
            target.insertAdjacentElement('afterend', promise);
          } else {
            container.prepend(promise);
          }
        });
      });

      const promiseText = this.promiseText();
      document.querySelectorAll('[data-pm-hub-promise-text]').forEach((element) => {
        if (element.textContent === promiseText) return;
        element.textContent = promiseText;
      });
    }
  }

  const initialize = () => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.pmHubInitialized === 'true') return;
      root.dataset.pmHubInitialized = 'true';
      try {
        new PurefectHubSelector(root);
        root.dataset.pmHubReady = 'true';
      } catch (_error) {
        root.removeAttribute('data-pm-hub-initialized');
      }
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', initialize);
})();



